import { useReadContracts } from "wagmi";
import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { usePassportContext } from "./usePassportContext";
import { mainnetTokenContracts, testnetTokenContracts, } from "../lib/contracts";
import useWalletAccount from "./useWalletAccount";
import { CHAIN_ID } from "../constants";
// Wagmi handles typesafety with ABI const assertions. TypeScript doesn't
// support importing JSON as const yet so types cannot be inferred from the
// imported contract. As a workaround there is minimal ABI definition that can
// be asserted types from.
// Ref: https://wagmi.sh/core/typescript#const-assert-abis-typed-data
const BALANCE_OF_ABI = [
    {
        inputs: [
            {
                internalType: "address",
                name: "account",
                type: "address",
            },
        ],
        name: "balanceOf",
        outputs: [
            {
                internalType: "uint256",
                name: "",
                type: "uint256",
            },
        ],
        stateMutability: "view",
        type: "function",
    },
];
const BALANCE_TOKENS = [
    "mcbBTC",
    "mDAI",
    "mFBTC",
    "mSolvBTC",
    "mswBTC",
    "mT",
    "mUSDC",
    "mUSDe",
    "mUSDT",
    "mxSolvBTC",
    "MUSD",
];
/**
 * Hook to get the balance of a list of tokens for the current account
 * @param   options.tokens The list of tokens to get the balance for. It will
 *          fallback to all tokens if not provided.
 * @param   options.queryOptions The query options to pass to the
 *          `useReadContracts`
 * @returns Tanstack's `useQuery` returnings with balance of tokens for the
 *          current account in form of typesafe object with token names as keys
 *          and balances as values.
 * @dev     In case of error data fallbacks to `0n`. To determine if an error
 *          occurred, check the `isError` property.
 * @example
 * const mezoTokensBalance = useTokensBalances({
 *   tokens: ["mT", "mxSolvBTC"],
 * })
 * console.log(mezoTokensBalance?.data.mT) // Eg. 0n
 * console.log(Object.keys(mezoTokensBalance?.data)) // ["mT", "mxSolvBTC"]
 */
export function useTokensBalances(options = {}) {
    const { environment = "mainnet" } = usePassportContext();
    const { accountAddress } = useWalletAccount();
    const contractsMap = useMemo(() => {
        const contracts = environment === "mainnet" ? mainnetTokenContracts : testnetTokenContracts;
        const tokenContracts = Object.fromEntries(Object.entries(contracts).filter(([key]) => BALANCE_TOKENS.includes(key)));
        return tokenContracts;
    }, [environment]);
    const { tokens = Object.keys(contractsMap), queryOptions = {} } = options;
    const contracts = useMemo(() => {
        if (!accountAddress)
            return [];
        return tokens.map((token) => {
            const { address } = contractsMap[token];
            return {
                address,
                abi: BALANCE_OF_ABI,
                functionName: "balanceOf",
                args: [accountAddress],
                chainId: CHAIN_ID[environment],
            };
        });
    }, [accountAddress, tokens, contractsMap, environment]);
    return useReadContracts({
        contracts,
        query: {
            select: (data) => data.reduce((acc, item) => {
                const token = tokens[data.indexOf(item)];
                return {
                    ...acc,
                    [token]: item.result ?? 0n,
                };
            }, {}),
            ...queryOptions,
        },
    });
}
/**
 * Hook for invalidating current user's token balances. Can be used to
 * invalidate the balances manually, which forces the data to be re-fetched.
 * @param tokens The list of tokens (as string arrays) for which we want to
 * invalidate query for.
 * @returns Function `invalidateTokenBalances` that invalidates token balances
 * @example
 * const { invalidateTokenBalances } = useInvalidateTokensBalances(["mT", "mUSD"])
 * (...)
 * await invalidateTokenBalances()
 */
export function useInvalidateTokensBalances(tokens) {
    const queryClient = useQueryClient();
    const { queryKey } = useTokensBalances({ tokens });
    const invalidateTokensBalancesHandler = useCallback(() => queryClient.invalidateQueries({ queryKey }), [queryClient, queryKey]);
    return {
        invalidateTokensBalances: invalidateTokensBalancesHandler,
    };
}
/**
 * Hook for resetting current user's token balances. Can be used to reset the
 * balances manually, which forces the data to be re-fetched.
 * @param tokens The list of tokens (as string arrays) for which we want to
 * reset query for.
 * @returns Function `resetTokenBalances` that invalidates token balances
 * @example
 * const { resetTokenBalances } = useResetTokensBalances(["mT", "mUSD"])
 * (...)
 * await resetTokenBalances()
 */
export function useResetTokensBalances(tokens) {
    const queryClient = useQueryClient();
    const { queryKey } = useTokensBalances({ tokens });
    const resetTokenBalancesHandler = useCallback(() => queryClient.resetQueries({ queryKey }), [queryClient, queryKey]);
    return {
        resetTokenBalances: resetTokenBalancesHandler,
    };
}
//# sourceMappingURL=useTokensBalances.js.map